import { cn } from '@/lib/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import styles from './Button.module.css';

const buttonVariants = cva(styles.button, {
  variants: {
    size: {
      sm: styles.buttonSmall,
      md: styles.buttonMedium,
      lg: styles.buttonLarge,
      xl: styles.buttonXLarge,
      xxl: styles.button2XLarge,
    },
    hierarchy: {
      'secondary-gray': styles.buttonSecondaryGray,
      'secondary-color': styles.buttonSecondaryColor,
      'ternary-gray': styles.buttonTernaryGray,
      'ternary-color': styles.buttonTernaryColor,
      'link-gray': styles.buttonLinkGray,
      'link-color': styles.buttonLinkColor,
    },
  },
  defaultVariants: {
    size: 'sm',
    hierarchy: null,
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  IconLeading?: React.ReactNode;
  IconTrailing?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, hierarchy, size, IconTrailing, IconLeading, ...props }, ref) => {
    const isOnlyOneIcon = (IconLeading && !IconTrailing) || (!IconLeading && IconTrailing);
    return (
      <button
        className={cn(
          buttonVariants({ size, hierarchy, className }),
          isOnlyOneIcon ? styles.buttonOnlyOneIcon : null
        )}
        ref={ref}
        {...props}
      >
        {IconLeading}
        <span>{props.children}</span>
        {IconTrailing}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
